import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

// Image imports
import ext1 from '../assets/landing/extension/1.png';
import ext2 from '../assets/landing/extension/2.png';
import webExercise from '../assets/landing/web_site/exercise.png';
import webWordsFull from '../assets/landing/web_site/words_full_mode.png';
import mobileVocab from '../assets/landing/mobile/vocabulary.png';
import mobileWord from '../assets/landing/mobile/word.png';
import mobileExercise from '../assets/landing/mobile/exercise.png';

const ChromeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#4285f4"/>
    <circle cx="12" cy="12" r="4" fill="white"/>
    <path d="M12 8h8.7M5.3 17l4.35-7.5M13.65 17H5.3" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const Check = () => <span className="lp-check">✓</span>;

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/memomizer-quick-add/hmniehlgampnhhplbdnpaakfbcdakaol';

export default function LandingPage() {
  const revealRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('lp-visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.lp-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lp" ref={revealRef}>

      {/* ── Nav ─────────────────────────────────────────── */}
      <nav className="lp-nav">
        <a href="/" className="lp-logo">
          <div className="lp-mark">M</div>
          Memomizer
        </a>
        <div className="lp-nav-right">
          <Link to="/login" className="lp-btn lp-btn-ghost">Sign in</Link>
          <a href={CHROME_STORE_URL} className="lp-btn lp-btn-outline" target="_blank" rel="noreferrer">
            Add to Chrome
          </a>
          <Link to="/signup" className="lp-btn lp-btn-primary">Get started free</Link>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-blob lp-blob-1" />
        <div className="lp-blob lp-blob-2" />
        <div className="lp-hero-inner">
          <div className="lp-badge">
            <span className="lp-badge-dot" />
            Chrome Extension — Free to install
          </div>
          <h1>
            Learn every word you read —<br />
            <span className="lp-hl">without breaking your flow</span>
          </h1>
          <p>
            Memomizer highlights your vocabulary as you browse, captures new words in one click,
            and builds personalized exercises so words actually stick.
          </p>
          <div className="lp-ctas">
            <Link to="/signup" className="lp-btn lp-btn-lg lp-btn-white">
              Create account →
            </Link>
            <a href={CHROME_STORE_URL} className="lp-btn lp-btn-lg lp-btn-glass" target="_blank" rel="noreferrer">
              <ChromeIcon /> Add to Chrome
            </a>
          </div>

          {/* Browser mockup */}
          <div className="lp-browser">
            <div className="lp-browser-bar">
              <div className="lp-dots">
                <div className="lp-dot lp-dot-r" />
                <div className="lp-dot lp-dot-y" />
                <div className="lp-dot lp-dot-g" />
              </div>
              <div className="lp-url">en.wikipedia.org/wiki/Bus</div>
            </div>
            <img src={ext2} alt="Memomizer showing word popup with example sentences on Wikipedia" />
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────── */}
      <section className="lp-section-alt">
        <div className="lp-container">
          <div className="lp-steps-header lp-reveal">
            <span className="lp-eyebrow">How it works</span>
            <h2 className="lp-title">Three steps to a richer vocabulary</h2>
            <p className="lp-sub">Fits into your natural reading habit — no separate study sessions required.</p>
          </div>
          <div className="lp-steps">
            <div className="lp-step lp-reveal lp-d1">
              <div className="lp-step-num">1</div>
              <h3>Capture while you browse</h3>
              <p>Select any word on any webpage and right-click to save it. A floating icon appears on hover too. Done in one second, without leaving the page.</p>
            </div>
            <div className="lp-step lp-reveal lp-d2">
              <div className="lp-step-num">2</div>
              <h3>AI creates your examples</h3>
              <p>Get 2–3 example sentences personalized to your interests — travel, coding, music, whatever you care about. Not generic dictionary sentences.</p>
            </div>
            <div className="lp-step lp-reveal lp-d3">
              <div className="lp-step-num">3</div>
              <h3>Practice in 5 minutes</h3>
              <p>AI writes a short story using your words and turns them into fill-in-the-blank challenges. Perfect for a coffee break.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature: Extension ──────────────────────────── */}
      <section className="lp-section">
        <div className="lp-container">
          <div className="lp-feature">
            <div className="lp-reveal">
              <span className="lp-eyebrow">Chrome Extension</span>
              <h2 className="lp-title">Your vocabulary, always visible</h2>
              <p className="lp-sub">Every word you've saved is automatically highlighted as you browse — so you encounter your vocabulary in real context, every day.</p>
              <ul className="lp-checks">
                <li><Check />Words highlighted on every page you visit</li>
                <li><Check />Click any highlight to see examples and track progress</li>
                <li><Check />Right-click or hover-select to capture new words instantly</li>
                <li><Check />Customizable highlight color and opacity</li>
              </ul>
            </div>
            <div className="lp-reveal lp-d1">
              <div className="lp-ext">
                <div className="lp-ext-back lp-ss">
                  <img src={ext1} alt="Words highlighted in yellow on a Wikipedia article" />
                </div>
                <div className="lp-ext-front">
                  <img src={ext2} alt="Word popup showing example sentences and learning level" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature: Vocabulary dashboard ───────────────── */}
      <section className="lp-section-alt">
        <div className="lp-container">
          <div className="lp-feature lp-flip">
            <div className="lp-reveal">
              <span className="lp-eyebrow">Web App</span>
              <h2 className="lp-title">Your personal vocabulary dashboard</h2>
              <p className="lp-sub">Review all your words with AI-generated example sentences, track learning progress, and stay organized.</p>
              <ul className="lp-checks">
                <li><Check />Personalized examples based on your interests</li>
                <li><Check />Mark each word as Learning, Hard, or Mastered</li>
                <li><Check />Auto-translation into your native language</li>
                <li><Check />Search, filter and group words by date</li>
              </ul>
            </div>
            <div className="lp-reveal lp-d1">
              <div className="lp-ss">
                <img src={webWordsFull} alt="Vocabulary list showing words with personalized example sentences" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature: Exercise ───────────────────────────── */}
      <section className="lp-section">
        <div className="lp-container">
          <div className="lp-feature">
            <div className="lp-reveal">
              <span className="lp-eyebrow">Quick Exercise</span>
              <h2 className="lp-title">5-minute stories that make words stick</h2>
              <p className="lp-sub">AI writes a short story using your vocabulary words and turns them into fill-in-the-blank challenges. Reveal answers whenever you're ready.</p>
              <ul className="lp-checks">
                <li><Check />Story tailored to your interests, not random text</li>
                <li><Check />Uses only words you're actively learning</li>
                <li><Check />Reveal individual words or the full story</li>
                <li><Check />Fresh exercise every time with one click</li>
              </ul>
            </div>
            <div className="lp-reveal lp-d1">
              <div className="lp-ss">
                <img src={webExercise} alt="Quick Exercise fill-in-the-blank story interface" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mobile ──────────────────────────────────────── */}
      <section className="lp-mobile">
        <div className="lp-container">
          <div className="lp-mobile-inner lp-reveal">
            <span className="lp-eyebrow">Mobile Ready</span>
            <h2 className="lp-title">Learn anywhere, anytime</h2>
            <p className="lp-sub">The web app is fully responsive. Review words, run exercises, and track progress from your phone — on the bus, in a waiting room, anywhere.</p>
          </div>
          <div className="lp-phones">
            <div className="lp-phone lp-reveal lp-d1">
              <img src={mobileVocab} alt="Vocabulary list on mobile" />
            </div>
            <div className="lp-phone lp-phone-tall lp-reveal lp-d2">
              <img src={mobileWord} alt="Word detail view on mobile" />
            </div>
            <div className="lp-phone lp-reveal lp-d3">
              <img src={mobileExercise} alt="Quick Exercise on mobile" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────── */}
      <section className="lp-cta-section">
        <h2>Start building your vocabulary today</h2>
        <p>Free Chrome extension. No credit card. Works on every website you already read.</p>
        <div className="lp-ctas">
          <Link to="/signup" className="lp-btn lp-btn-lg lp-btn-white">
            Create free account
          </Link>
          <a href={CHROME_STORE_URL} className="lp-btn lp-btn-lg lp-btn-glass" target="_blank" rel="noreferrer">
            <ChromeIcon /> Add to Chrome
          </a>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <a href="/" className="lp-footer-logo">
            <div className="lp-mark" style={{ width: 28, height: 28, fontSize: 13 }}>M</div>
            Memomizer
          </a>
          <div>© 2026 Memomizer. All rights reserved.</div>
          <div className="lp-footer-links">
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
